import { useMemo } from "react";
import {
  getLastArtifactId,
  SessionItem,
  SessionItemKind,
  SessionRenderingContext,
} from "../session-item";
import { SessionStatus, MDSSessionConfig } from "../session.types";
import { SessionViewMode } from "../session.constants";

/**
 * Splices the "New" divider above the item carrying `unreadDividerEventId`.
 *
 * Anchored by event id rather than by comparing timestamps: items are ordered
 * by `internal_updated_at` while the unread watermark is in `locally_created_at`,
 * so a timestamp-derived position could land a row off. The id is resolved
 * against whatever order the list already has.
 *
 * The caller must latch the id when the surface opens. Reading the live
 * first-unread event would make the divider disappear the moment the session is
 * marked seen, which happens within a tick of opening.
 */
export const useSessionItemsWithUnreadDivider = ({
  sessionItems,
  unreadDividerEventId,
  isUnreadDividerRetired,
}: {
  sessionItems: SessionItem[];
  unreadDividerEventId?: string;
  isUnreadDividerRetired?: boolean;
}): SessionItem[] =>
  useMemo(() => {
    if (!unreadDividerEventId) return sessionItems;

    const anchorIndex = sessionItems.findIndex(item =>
      item.data.some(itemData => itemData.context.eventId === unreadDividerEventId)
    );
    // The anchor event may not be rendered: still loading, or filtered out as a
    // non-renderable kind. Returning the input by identity keeps surfaces that
    // never set the field free of extra array churn.
    if (anchorIndex === -1) return sessionItems;

    // A retired divider keeps its row and fades in place. Removing it would
    // reflow every message below by the row's height.
    const divider: SessionItem = {
      id: `unread-divider-${unreadDividerEventId}`,
      kind: SessionItemKind.UnreadDivider,
      data: [],
      context: { isExpanded: false, isRetired: isUnreadDividerRetired ?? false },
    };

    return [
      ...sessionItems.slice(0, anchorIndex),
      divider,
      ...sessionItems.slice(anchorIndex),
    ];
  }, [sessionItems, unreadDividerEventId, isUnreadDividerRetired]);

export const useSessionRenderContext = ({
  sessionStatus,
  sessionViewMode,
  sessionItems,
  sessionConfig,
}: {
  sessionStatus: SessionStatus;
  sessionViewMode: SessionViewMode;
  sessionItems: SessionItem[];
  sessionConfig: MDSSessionConfig;
}): SessionRenderingContext => {
  const config = useMemo(
    () => ({
      openLinksInNewTab: sessionConfig.openLinksInNewTab,
      prohibitNativeCmdClick: sessionConfig.prohibitNativeCmdClick,
      linkHandler: sessionConfig.linkHandler,
      onMemItemOver: sessionConfig.onMemItemOver,
      onMemItemOut: sessionConfig.onMemItemOut,
      feedbackHandler: sessionConfig.feedbackHandler,
      features: sessionConfig.features,
      resolveFactSource: sessionConfig.resolveFactSource,
      resolveAssetProxy: sessionConfig.resolveAssetProxy,
      onClickLink: sessionConfig.onClickLink,
      threads: sessionConfig.threads,
    }),
    [
      sessionConfig.openLinksInNewTab,
      sessionConfig.linkHandler,
      sessionConfig.onMemItemOver,
      sessionConfig.onMemItemOut,
      sessionConfig.feedbackHandler,
      sessionConfig.prohibitNativeCmdClick,
      sessionConfig.features,
      sessionConfig.resolveFactSource,
      sessionConfig.resolveAssetProxy,
      sessionConfig.onClickLink,
      sessionConfig.threads,
    ]
  );

  return useMemo(
    () => ({
      status: sessionStatus,
      sessionViewMode,
      config,
      lastReasoningGroupId: (() => {
        const last = sessionItems
          .slice()
          .reverse()
          .find(item => item.kind === SessionItemKind.ReasoningGroup);
        return last?.id ?? null;
      })(),
      lastItemId: sessionItems.length > 0 ? sessionItems[sessionItems.length - 1].id : null,
      lastArtifactId: getLastArtifactId(sessionItems),
    }),
    [sessionStatus, sessionViewMode, config, sessionItems]
  );
};
